import type { ReactElement } from "react";
import type { LiftableAction } from "odyssey-shared";
import { ConfirmDialog, TextField } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { MODERATION_LAYOUT } from "./layout.ts";

/**
 * The Moderation window's two dialogs (the design's `moderation-window` card, its states `confirm` and `end`, a
 * visual reference): the ban's confirmation, which restates who, what, how long and the words the player reads, and
 * the end of a ban or a freeze, which asks for a staff-only reason before it is recorded (a reason is required of
 * every action on the wire). Each is the kit's confirm dialog, a later sibling of the window in the same stage, so
 * Escape goes back to the window and Enter confirms.
 */

/**
 * What the ban's confirmation restates.
 */
export interface BanAsked {
  readonly family: string;
  readonly firsts: readonly string[];
  readonly length: string;
  readonly ends: string | null;
  readonly reason: string;
  readonly note: string;
}

/**
 * The ban's confirmation.
 *
 * @param props What is `asked`; whether the request is `busy`; `onConfirm`, which sends it; and `onBack`.
 * @returns The dialog.
 */
export function BanConfirm({ asked, busy, onConfirm, onBack }: { readonly asked: BanAsked; readonly busy: boolean; readonly onConfirm: () => void; readonly onBack: () => void }): ReactElement {
  const line = (term: string, value: string): ReactElement => (
    <div className="ot-moderation__confirm-line" key={term}>
      <span className="ot-moderation__term">{term}</span>
      <span>{value}</span>
    </div>
  );
  return (
    <ConfirmDialog title={say("moderation.window.confirmTitle", { family: asked.family })} confirmLabel={asked.ends === null ? say("moderation.window.go.banPermanentConfirm") : say("moderation.window.go.banConfirm", { length: asked.length })} cancelLabel={say("moderation.window.back")} onConfirm={onConfirm} onCancel={onBack} busy={busy} width={MODERATION_LAYOUT.confirmWidth}>
      <div className="ot-moderation__confirm" data-moderation="confirm">
        {line(say("moderation.window.confirmWho"), asked.firsts.length === 0 ? say("moderation.window.confirmFamily", { family: asked.family }) : say("moderation.window.confirmFamilyFirsts", { family: asked.family, firsts: asked.firsts.reduce((first, second) => say("moderation.window.confirmBoth", { first, second })) }))}
        {line(say("moderation.window.confirmWhat"), say("moderation.window.confirmBan"))}
        {line(say("moderation.window.confirmLong"), asked.ends === null ? say("moderation.window.permanent") : say("moderation.window.confirmUntil", { length: asked.length, when: asked.ends }))}
        <div className="ot-moderation__reads">
          <span className="ot-moderation__reads-title">{say("moderation.window.confirmReads", { family: asked.family })}</span>
          <span>{say("moderation.window.quoted", { text: asked.reason })}</span>
        </div>
        <p className="ot-moderation__muted ot-moderation__small">{[asked.note.trim() === "" ? null : say("moderation.window.confirmNote", { family: asked.family, note: asked.note.trim() }), say("moderation.window.confirmEnd")].filter((part) => part !== null).join(" ")}</p>
      </div>
    </ConfirmDialog>
  );
}

/**
 * What an End button asks to end.
 */
export interface EndAsked {
  readonly id: string;
  readonly action: LiftableAction;
  readonly family: string;
  readonly left: string;
}

/**
 * The end of a ban or a freeze.
 *
 * @param props What is `asked`; the `reason` typed and `onReason`; whether the request is `busy`; the page's own
 *   `error` for a reason it refuses, or undefined; `onConfirm`, which sends it; and `onBack`.
 * @returns The dialog.
 */
export function EndConfirm({ asked, reason, onReason, busy, error, onConfirm, onBack }: { readonly asked: EndAsked; readonly reason: string; readonly onReason: (reason: string) => void; readonly busy: boolean; readonly error: string | undefined; readonly onConfirm: () => void; readonly onBack: () => void }): ReactElement {
  return (
    <ConfirmDialog title={say(`moderation.window.endTitle.${asked.action}`)} confirmLabel={say(`moderation.window.end.${asked.action}`)} cancelLabel={say("moderation.window.back")} onConfirm={onConfirm} onCancel={onBack} busy={busy} error={error} width={MODERATION_LAYOUT.endWidth} tone="primary">
      <div className="ot-moderation__confirm" data-moderation="end">
        <p className="ot-moderation__text">{asked.action === "freeze" ? say("moderation.window.endFreezeBody", { family: asked.family, left: asked.left }) : say("moderation.window.endBanBody", { family: asked.family })}</p>
        <TextField label={say("moderation.window.endReason")} value={reason} onChange={onReason} maxLength={MODERATION_LAYOUT.textLimit} hint={asked.action === "freeze" ? say("moderation.window.endHint.freeze", { family: asked.family }) : say("moderation.window.endHint.ban")} />
      </div>
    </ConfirmDialog>
  );
}
